import React, { useState, useEffect } from 'react';
import { Input } from '../../ui/input';
import { useLazyCommand } from '@soap/modules';
import { Loader2, Edit2 } from 'lucide-react';
import type { TopicInlineEditTarget } from './InlineTextFieldEditor';

interface InlineHoursEditorProps {
  minHours: number | null | undefined;
  topicId: string;
  studentId: string | null;
  editTarget?: TopicInlineEditTarget;
  onUpdate?: (newHours: number | null) => void;
}

export function InlineHoursEditor({
  minHours,
  topicId,
  studentId,
  editTarget = 'student',
  onUpdate
}: InlineHoursEditorProps) {
  const safeHours = minHours ?? 0;
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(safeHours > 0 ? safeHours.toString() : '');
  const [isSaving, setIsSaving] = useState(false);
  const [updateStudentMinHours] = useLazyCommand("C289v1_UpdateTopicMinHours");
  const [updateTenantCatalogMinHours] = useLazyCommand("C321v1_UpdateTenantCustomCatalogTopicMinHours");

  const canEdit =
    editTarget !== 'none' &&
    !!topicId &&
    (editTarget === 'tenant' || (!!studentId && editTarget === 'student'));

  useEffect(() => {
    setEditValue(safeHours > 0 ? safeHours.toString() : '');
  }, [safeHours]);

  const handleSave = async () => {
    if (!topicId || !canEdit) {
      setEditing(false);
      return;
    }

    const trimmedValue = editValue.trim();
    let numValue: number | null = null;

    if (trimmedValue === '') {
      numValue = null;
    } else {
      numValue = Number(trimmedValue);
      if (isNaN(numValue) || numValue < 0 || numValue > 1000) {
        setEditValue(safeHours > 0 ? safeHours.toString() : '');
        setEditing(false);
        return;
      }
    }

    setIsSaving(true);
    try {
      if (editTarget === 'tenant') {
        await updateTenantCatalogMinHours({
          $type: "C321v1_UpdateTenantCustomCatalogTopicMinHours",
          C321_TopicId: topicId,
          C321_MinHours: numValue !== null ? numValue : undefined
        });
      } else if (editTarget === 'student' && studentId) {
        await updateStudentMinHours({
          $type: "C289v1_UpdateTopicMinHours",
          C289_StudentId: studentId,
          C289_TopicId: topicId,
          C289_MinHours: numValue !== null ? numValue : undefined
        });
      } else {
        setEditing(false);
        setIsSaving(false);
        return;
      }

      if (onUpdate) {
        onUpdate(numValue);
      }
      setEditing(false);
    } catch (error) {
      console.error('Failed to update topic hours:', error);
      setEditValue(safeHours > 0 ? safeHours.toString() : '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(safeHours > 0 ? safeHours.toString() : '');
      setEditing(false);
    }
  };

  const trimmedValue = editValue.trim();
  const numValue = trimmedValue === '' ? null : Number(trimmedValue);
  const isValid = trimmedValue === '' || (!isNaN(numValue!) && numValue! >= 0 && numValue! <= 1000);

  if (!canEdit) {
    if (!minHours || minHours === 0) {
      return <span className="text-sm text-gray-500">Not set</span>;
    }
    return <span className="text-sm font-semibold text-gray-900">{minHours} hours</span>;
  }

  if (editing) {
    return (
      <div className="flex flex-nowrap items-center gap-2">
        <Input
          type="number"
          min={0}
          max={1000}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          className={`w-20 h-7 text-sm ${!isValid ? 'border-destructive-action focus-visible:ring-destructive-action focus-visible:ring-2' : ''}`}
          autoFocus
          placeholder="0"
        />
        <span className="text-sm text-gray-500">hours</span>
        {isSaving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>
    );
  }

  if (!minHours || minHours === 0) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex flex-nowrap items-center gap-1.5 px-2 py-1 hover:bg-action-blue/10 hover:border-action-blue/40 border border-transparent rounded text-sm font-semibold text-gray-600 transition-colors group"
        disabled={isSaving || !canEdit}
        title="Click to set hours"
      >
        <span>Set hours</span>
        <Edit2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-action-blue transition-colors" />
      </button>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="flex flex-nowrap items-center gap-1.5 px-2 py-1 hover:bg-action-blue/10 hover:border-action-blue/40 border border-transparent rounded text-sm font-semibold text-gray-900 transition-colors group"
      disabled={isSaving || !canEdit}
      title="Click to edit hours"
    >
      <span>{minHours} hours</span>
      <Edit2 className="h-3.5 w-3.5 text-gray-400 group-hover:text-action-blue transition-colors" />
    </button>
  );
}
